/**
 * Bending Moment Diagram (BMD) Visualization Component
 * 
 * Renders BMD as translucent orange solid with dark orange outline
 */

import React, { useMemo } from 'react';
import * as THREE from 'three';
import { 
  BMDData, 
  BeamProperties, 
  generateBMDClosedGeometry,
  generateBMDCylindricalOutline
} from '../../utils/bmdUtils';

interface BMDVisualizationProps {
  bmdData: BMDData;
  beam: BeamProperties;
  visible?: boolean;
  scale?: number; // scale factor for BMD height (m per kN·m)
  fillColor?: string;
  outlineColor?: string;
  opacity?: number;
}

export const BMDVisualization: React.FC<BMDVisualizationProps> = ({
  bmdData,
  beam,
  visible = true,
  scale = 0.01,
  fillColor = '#FFA500', // Orange
  outlineColor = '#F5330C', // Dark golden rod - much darker and bolder
  opacity = 0.3
}) => {
  // Generate proper closed geometry for filled BMD area
  const bmdGeometry = useMemo(() => {
    if (!visible) return null;
    
    const { vertices, indices } = generateBMDClosedGeometry(bmdData, beam, scale);
    
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    
    return geometry;
  }, [bmdData, beam, scale, visible]);
  
  // Generate cylindrical outline geometry for pipe-like appearance
  const outlineGeometry = useMemo(() => {
    if (!visible) return null;
    
    const { vertices, indices } = generateBMDCylindricalOutline(
      bmdData, 
      beam, 
      scale, 
      0.03 // Slightly larger tube radius for more prominent pipe appearance
    );
    
    if (vertices.length === 0) return null;
    
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    
    return geometry;
  }, [bmdData, beam, scale, visible]);
  
  if (!visible) return null;
  
  return (
    <group>
      {/* Filled BMD area */}
      {bmdGeometry && (
        <mesh geometry={bmdGeometry}>
          <meshBasicMaterial
            color={fillColor}
            transparent
            opacity={opacity}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
      
      {/* Cylindrical BMD outline */}
      {outlineGeometry && (
        <mesh geometry={outlineGeometry}>
          <meshBasicMaterial
            color={outlineColor}
            transparent
            opacity={1}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
      
      {/* Optional: Add text label for max moment */}
      {/* TODO: Add 3D text or HTML overlay for moment values */}
    </group>
  );
};

export default BMDVisualization;
